import { z } from 'zod';
import { IsoDate, MoneyAmount } from '../primitives.js';
import { CounterpartyKind } from './counterparty.js';
import { PaymentMethod } from './payment.js';

/**
 * S15-BE.1 — Cash-flow DTO (actual + forecast).
 * Actual — sum payments за период. Forecast — sum invoice.outstanding в окне [today..+90d].
 */

export const CashFlowDirection = z.enum(['inflow', 'outflow']);
export type CashFlowDirection = z.infer<typeof CashFlowDirection>;

export const CashFlowSegment = z.enum(['operating', 'investing', 'financing']);
export type CashFlowSegment = z.infer<typeof CashFlowSegment>;

export const CashFlowPeriod = z.object({
  date_from: IsoDate,
  date_to: IsoDate,
});
export type CashFlowPeriod = z.infer<typeof CashFlowPeriod>;

export const CashFlowActualRow = z.object({
  period: CashFlowPeriod,
  direction: CashFlowDirection,
  method: PaymentMethod,
  counterparty_kind: CounterpartyKind,
  amount: MoneyAmount,
  payment_count: z.number().int(),
});
export type CashFlowActualRow = z.infer<typeof CashFlowActualRow>;

export const CashFlowForecastBucket = z.enum(['overdue', '0_30', '31_60', '61_90']);
export type CashFlowForecastBucket = z.infer<typeof CashFlowForecastBucket>;

export const CashFlowForecastRow = z.object({
  bucket: CashFlowForecastBucket,
  direction: CashFlowDirection,
  counterparty_kind: CounterpartyKind,
  expected_amount: MoneyAmount,
  invoice_count: z.number().int(),
});
export type CashFlowForecastRow = z.infer<typeof CashFlowForecastRow>;

export const CashFlowTotals = z.object({
  inflow: MoneyAmount,
  outflow: MoneyAmount,
  net: MoneyAmount,
});
export type CashFlowTotals = z.infer<typeof CashFlowTotals>;

export const CashFlowForecastTotals = z.object({
  expected_inflow: MoneyAmount,
  expected_outflow: MoneyAmount,
  net_expected: MoneyAmount,
});
export type CashFlowForecastTotals = z.infer<typeof CashFlowForecastTotals>;

/**
 * U-5 (integrations F8) — drill фактического cash-flow по статье/группе/проекту.
 * Adesk-коннектор пишет cf_article/cf_group/project_tag в payments; раньше страница их не
 * показывала. Суммы — в KZT (нормализация по fx_rate_to_kzt), superseded-seed исключены (U-3).
 */
export const CashFlowArticleRow = z.object({
  /** null = «без статьи» (платёж без cf_article). */
  article: z.string().nullable(),
  group: z.string().nullable(),
  project_tag: z.string().nullable(),
  inflow_kzt: z.string(),
  outflow_kzt: z.string(),
  net_kzt: z.string(),
  payment_count: z.number().int(),
});
export type CashFlowArticleRow = z.infer<typeof CashFlowArticleRow>;

export const CashFlowReport = z.object({
  period: CashFlowPeriod,
  actual: z.array(CashFlowActualRow),
  forecast: z.array(CashFlowForecastRow),
  actual_totals: CashFlowTotals,
  forecast_totals: CashFlowForecastTotals,
  /** U-5 — drill по статьям/группам/проектам (может отсутствовать в старых снапшотах). */
  by_article: z.array(CashFlowArticleRow).optional(),
  schema_version: z.string(),
});
export type CashFlowReport = z.infer<typeof CashFlowReport>;

export const CashFlowForecastSparklinePoint = z.object({
  date: IsoDate,
  cumulative_net: MoneyAmount,
});
export type CashFlowForecastSparklinePoint = z.infer<typeof CashFlowForecastSparklinePoint>;

/** Dashboard tile (S15-BE.5). */
export const CashFlowForecastSummary = z.object({
  horizon_days: z.literal(30),
  expected_inflow: MoneyAmount,
  expected_outflow: MoneyAmount,
  net_expected: MoneyAmount,
  sparkline_points: z.array(CashFlowForecastSparklinePoint),
});
export type CashFlowForecastSummary = z.infer<typeof CashFlowForecastSummary>;
